import { CreateMyTextPresetArgs } from '@sparemin/api-sdk';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createTextPreset,
  deleteTextPreset,
} from 'redux/middleware/api/headliner-user-service';
import { pushModal } from 'redux/modules/modal';
import { showError } from 'redux/modules/notification';
import { isProSelector } from 'redux/modules/pricing';
import {
  getTextPresets,
  textPresetIdsSelector,
} from 'redux/modules/text-presets';
import { Dispatch } from 'redux/types';

interface UseTextPresetsResult {
  presetIds: [];
  isLoading: boolean;
  onSaveTextPreset: (config: CreateMyTextPresetArgs) => Promise<void>;
  onDeleteTextPreset: (textPresetId: string) => void;
}

export default function useTextPresets(): UseTextPresetsResult {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isPro = useSelector(isProSelector);
  const presetIds = useSelector(textPresetIdsSelector);

  const dispatch = useDispatch<Dispatch>();

  const handleSavePreset = useCallback(
    async (config: CreateMyTextPresetArgs): Promise<void> => {
      if (!isPro) {
        dispatch(pushModal({ name: 'SaveCustomCaptionStyleUpsell' }));

        return;
      }

      setIsLoading(true);

      try {
        await dispatch(createTextPreset(config));
        await dispatch(getTextPresets());
      } catch (error) {
        dispatch(
          showError({
            title: 'Error saving preset',
            message: 'Please try again',
            dismissAfterSec: 5,
          }),
        );
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, isPro],
  );

  const handleDeletePreset = useCallback(
    async (textPresetId: string): Promise<void> => {
      setIsLoading(true);

      try {
        await dispatch(
          deleteTextPreset({
            textPresetId,
          }),
        );
        await dispatch(getTextPresets());
      } catch (error) {
        dispatch(
          showError({
            title: 'Error deleting preset',
            message: 'Please try again',
            dismissAfterSec: 5,
          }),
        );
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(getTextPresets());
  }, [dispatch]);

  return {
    presetIds,
    isLoading,
    onSaveTextPreset: handleSavePreset,
    onDeleteTextPreset: handleDeletePreset,
  };
}
